import { PersonalInfoData } from "@faire/retailer-visitor-shared/features/SignUp/SignUpRequestData";
import { useSSOStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { useSignUpQueryParams } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpQueryParams";
import { useStrictLocalization } from "@faire/web/common/localization";
import checkUserAvailability from "@faire/web-api--source/endpoints/www/api/user/availability/post";
import { useMutation as useValidateSignUp } from "@faire/web-api--source/endpoints/www/api/user/validate-sign-up/post-hooks";
import { ICheckEmailAddressAndBusinessAvailabilityRequest } from "@faire/web-api--source/indigofair/data/ICheckEmailAddressAndBusinessAvailabilityRequest";
import { IValidateSignUpRequest } from "@faire/web-api--source/indigofair/data/IValidateSignUpRequest";

export const EMAIL_UNAVAILABLE_ERROR = "EMAIL_UNAVAILABLE_ERROR";

export const useServerValidatePersonalInfo = () => {
  const { strictLocalize } = useStrictLocalization();

  const { isRetailerSubUser } = useSignUpQueryParams();
  const { isActive } = useSSOStore();

  const { mutateAsync: validateSignUp } = useValidateSignUp();

  const serverValidatePersonalInfo: (
    values: PersonalInfoData,
    setFieldError: (field: string, message: string | undefined) => void
  ) => Promise<boolean> = async (values, setFieldError) => {
    // Validate Email Address
    if (!isRetailerSubUser) {
      try {
        const { email_address_available } = await checkUserAvailability(
          ICheckEmailAddressAndBusinessAvailabilityRequest.build({
            email_address: values.email_address,
          })
        );

        if (!email_address_available) {
          setFieldError("email_address", EMAIL_UNAVAILABLE_ERROR);
          return false;
        }
      } catch (error) {
        setFieldError(
          "email_address",
          strictLocalize({
            defaultMessage:
              "Unable to check if that email already exists, please try again",
          })
        );

        return false;
      }
    }

    // Validate Personal Info
    if (!(isActive && !isRetailerSubUser)) {
      const validationResponse = await validateSignUp([
        IValidateSignUpRequest.build({
          name: `${values.first_name} ${values.last_name}`,
          first_name: values.first_name,
          last_name: values.last_name,
          password: values.password,
          email_address: values.email_address,
        }),
      ]);

      if (validationResponse.failures?.length) {
        validationResponse.failures.forEach((failure) => {
          if (failure.field_name) {
            setFieldError(failure.field_name, failure.reason);
          }
        });

        return false;
      }
    }

    return true;
  };

  return { serverValidatePersonalInfo };
};
